import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeft, Clock, Mail, Shield, Link as LinkIcon, Megaphone, Image as ImageIcon, GalleryHorizontal, Video } from 'lucide-react';
import AdminSidebar from '../../../components/sidebar/AdminSidebar';
import LoginHistoryList from '../../../components/admin/detail/LoginHistoryList';
import GeneratedContentPanel from '../../../components/admin/detail/GeneratedContentPanel';
import UserUsageDashboard from '../../../components/admin/usage/UserUsageDashboard';
import adminUserDetailService, {
  AdminContentType,
  AdminUserDetail,
} from '../../../services/api/adminUserDetailService';
import { RootState } from '../../../redux/store';
import { cn } from '../../../lib/utils';
import {
  ResponsiveAppShell,
  TaskyBadge,
  TaskyButton,
  TaskyErrorState,
  TaskySpinner,
  TaskyStatCard,
} from '../../../components/tasky';

type UserTab = 'overview' | 'usage' | 'login-histories' | 'contents';

const TAB_ITEMS: { key: UserTab; label: string }[] = [
  { key: 'overview', label: '概要' },
  { key: 'usage', label: '使用状況' },
  { key: 'login-histories', label: 'ログイン履歴' },
  { key: 'contents', label: '生成物' },
];

const ROLE_LABELS: Record<string, string> = {
  leader: 'リーダー',
  member: 'メンバー',
};

const PLAN_LABELS: Record<string, string> = {
  demo: 'デモ',
  personal: 'パーソナル',
  team: 'チーム',
  agency: 'エージェンシー',
  enterprise: 'エンタープライズ',
  free: 'フリー',
};

const InfoLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-gray-400 mb-1">{children}</p>
);

const formatDateTime = (value: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString('ja-JP');
};

const AdminUserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = parseInt(id || '0', 10);
  const navigate = useNavigate();
  const { user: currentAdmin } = useSelector((state: RootState) => state.auth);

  const [activeTab, setActiveTab] = useState<UserTab>('overview');
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    adminUserDetailService
      .getUserDetail(userId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        console.error('Failed to fetch user detail:', err);
        if (!cancelled) setError(err?.message || 'ユーザー詳細の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const fetchLoginHistoryPage = useCallback(
    (page: number) => adminUserDetailService.getUserLoginHistories(userId, { page }),
    [userId]
  );

  const fetchContentPage = useCallback(
    (type: AdminContentType, page: number) => adminUserDetailService.getUserContents(userId, type, { page }),
    [userId]
  );

  const handleBack = () => {
    navigate('/admin');
  };

  const adminName = currentAdmin?.name || '管理者';
  const desktopSidebar = (
    <AdminSidebar
      activePage="users"
      onPageChange={(page) => navigate(`/admin${page === 'users' ? '' : `/${page}`}`)}
      adminName={adminName}
    />
  );
  const mobileSidebar = (
    <div className="flex min-h-full flex-col bg-dark py-4 text-white">
      <div className="px-5 pb-4">
        <p className="text-lg font-bold">管理パネル</p>
        <p className="mt-1 truncate text-xs text-white/60">{detail?.user.name || 'ユーザー詳細'}</p>
      </div>
      <button
        type="button"
        onClick={handleBack}
        className="mx-3 mb-3 rounded-tasky-sm border border-white/15 px-3 py-2 text-left text-sm text-white/[0.85]"
      >
        ユーザー一覧へ戻る
      </button>
      <nav className="min-h-0 flex-1 overflow-y-auto">
        {TAB_ITEMS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex w-full items-center px-5 py-3 text-left text-sm transition-colors ${
              activeTab === tab.key
                ? 'bg-white/[0.12] font-semibold text-white'
                : 'text-white/80 hover:bg-white/[0.08] hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="border-t border-white/10 px-5 pt-4">
        <p className="text-sm font-semibold">{adminName}</p>
        <p className="mt-1 text-xs text-white/60">管理者</p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <ResponsiveAppShell sidebar={desktopSidebar} mobileSidebar={mobileSidebar} topbarTitle="ユーザー詳細" className="bg-warm-bg">
        <div className="flex items-center gap-3 justify-center py-12 lg:ml-[250px]">
          <TaskySpinner size="md" />
          <span className="text-sm text-gray-600">データを読み込み中...</span>
        </div>
      </ResponsiveAppShell>
    );
  }

  if (error || !detail) {
    return (
      <ResponsiveAppShell sidebar={desktopSidebar} mobileSidebar={mobileSidebar} topbarTitle="ユーザー詳細" className="bg-warm-bg">
        <div className="p-6 lg:ml-[250px]">
          <TaskyErrorState
            title="ユーザー情報を取得できませんでした"
            description={error || 'ページを再読み込みしてもう一度お試しください。'}
          />
        </div>
      </ResponsiveAppShell>
    );
  }

  const { user, last_login_at, content_counts } = detail;

  return (
    <ResponsiveAppShell
      sidebar={desktopSidebar}
      mobileSidebar={mobileSidebar}
      topbarTitle="ユーザー詳細"
      className="bg-warm-bg"
    >
      <div className="min-h-[calc(100dvh-56px)] overflow-y-auto overflow-x-hidden bg-warm-bg lg:ml-[250px] lg:h-screen lg:min-h-0">
        <div className="mx-auto max-w-5xl px-3 py-4 sm:px-6 sm:py-6">
          {/* ヘッダー */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <TaskyButton variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft />
              戻る
            </TaskyButton>
            <h1 className="min-w-0 text-xl font-semibold text-dark sm:truncate">{user.name}</h1>
            {user.role && (
              <TaskyBadge variant="warm">{ROLE_LABELS[user.role] || user.role}</TaskyBadge>
            )}
            <TaskyButton
              variant="ghost"
              size="sm"
              className="sm:ml-auto"
              onClick={() => navigate(`/admin/behavior?tab=sessions&user_id=${user.id}`)}
            >
              行動ログ
            </TaskyButton>
          </div>

          {/* タブナビゲーション */}
          <div className="border-b border-warm-border mb-6">
            <div className="flex overflow-x-auto">
              {TAB_ITEMS.map(tab => (
                <div
                  key={tab.key}
                  className={cn(
                    'whitespace-nowrap pb-3 px-4 text-sm font-medium border-b-2 cursor-pointer transition-colors',
                    activeTab === tab.key
                      ? 'text-primary border-primary'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  )}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </div>
              ))}
            </div>
          </div>

          <div>
            {/* 概要タブ */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="rounded-tasky-md bg-white p-4 sm:p-6">
                  <h2 className="mb-5 text-sm font-semibold text-dark">プロフィール</h2>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                    <div>
                      <InfoLabel>名前</InfoLabel>
                      <p className="text-sm text-dark">{user.name}</p>
                    </div>
                    <div>
                      <InfoLabel>メールアドレス</InfoLabel>
                      <p className="flex items-center gap-1.5 text-sm text-dark">
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        {user.email}
                      </p>
                    </div>
                    <div>
                      <InfoLabel>ロール</InfoLabel>
                      <p className="flex items-center gap-1.5 text-sm text-dark">
                        <Shield className="h-3.5 w-3.5 text-gray-400" />
                        {user.role ? (ROLE_LABELS[user.role] || user.role) : <em className="text-gray-400 not-italic">未設定</em>}
                      </p>
                    </div>
                    <div>
                      <InfoLabel>プラン</InfoLabel>
                      <p className="text-sm text-dark">
                        {user.plan ? (PLAN_LABELS[user.plan] || user.plan) : <em className="text-gray-400 not-italic">未設定</em>}
                      </p>
                    </div>
                    <div>
                      <InfoLabel>所属組織</InfoLabel>
                      {user.organization ? (
                        <Link
                          to={`/admin/organizations/${user.organization.id}`}
                          className="flex items-center gap-1.5 text-sm text-primary hover:text-primary-hover"
                        >
                          <LinkIcon className="h-3.5 w-3.5" />
                          {user.organization.name}
                        </Link>
                      ) : (
                        <em className="text-sm text-gray-400 not-italic">未所属</em>
                      )}
                    </div>
                    <div>
                      <InfoLabel>登録日</InfoLabel>
                      <p className="text-sm text-dark">{formatDateTime(user.created_at)}</p>
                    </div>
                    <div>
                      <InfoLabel>初回ログイン</InfoLabel>
                      <p className="flex items-center gap-1.5 text-sm text-dark">
                        <Clock className="h-3.5 w-3.5 text-gray-400" />
                        {formatDateTime(user.first_login_at)}
                      </p>
                    </div>
                    <div>
                      <InfoLabel>最終ログイン</InfoLabel>
                      <p className="flex items-center gap-1.5 text-sm text-dark">
                        <Clock className="h-3.5 w-3.5 text-gray-400" />
                        {formatDateTime(last_login_at)}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="mb-3 text-sm font-semibold text-dark">生成物件数サマリ</h2>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <TaskyStatCard label="バナー" value={content_counts.banner} icon={<Megaphone />} iconTone="primary" />
                    <TaskyStatCard label="画像" value={content_counts.image} icon={<ImageIcon />} iconTone="success" />
                    <TaskyStatCard label="カルーセル" value={content_counts.carousel} icon={<GalleryHorizontal />} iconTone="purple" />
                    <TaskyStatCard label="動画" value={content_counts.video} icon={<Video />} iconTone="warning" />
                  </div>
                </div>
              </div>
            )}

            {/* 使用状況タブ */}
            {activeTab === 'usage' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">AI使用状況</h2>
                  <p className="text-xs text-gray-400 mt-1">このユーザーのAI使用量を確認できます（機能別）</p>
                </div>
                <div className="overflow-x-auto rounded-tasky-md bg-white p-4 sm:p-5">
                  <UserUsageDashboard userId={userId} />
                </div>
              </div>
            )}

            {/* ログイン履歴タブ */}
            {activeTab === 'login-histories' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">ログイン履歴</h2>
                </div>
                <LoginHistoryList fetchPage={fetchLoginHistoryPage} />
              </div>
            )}

            {/* 生成物タブ */}
            {activeTab === 'contents' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">生成物</h2>
                </div>
                <GeneratedContentPanel fetchPage={fetchContentPage} />
              </div>
            )}
          </div>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default AdminUserDetailPage;
